import { usePostCustomExerciseType } from '#api/exercise-types';
import { queryKeyFactory } from '#api/queryKeyFactory';
import {
  Input,
  InputWrapper
} from '#features/ScheduleRegistration/components/LectureRegistration/RowSearchMember/SearchLectureMember/SearchMemberForm/styles';
import { ButtonSubmit } from '#pages/WeeklyCalenderScheduleRegistration/styles';
import { useToast } from '#providers/ToastProvider';
import BottomSheet from '#shared/BottomSheet';
import Icon from '#shared/Icon';
import { SearchList } from '#shared/exercise/exercise-list/SearchList';
import { PrevHeaderBarWithTitleTemplate } from '#templates';
import { SetTypeKey } from '#types/exercisePresets';
import { Part } from '#types/exerciseTypes';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import Select from 'src/shared/components/select/Select';

interface AddCustomExerciseProps {
  openAddCustomExerciseModal: boolean;
  setOpenAddCustomExerciseModal: (open: boolean) => void;
}

export const AddCustomExercise = ({
  openAddCustomExerciseModal,
  setOpenAddCustomExerciseModal
}: AddCustomExerciseProps) => {
  const queryClient = useQueryClient();

  const { showToast } = useToast();

  // 운동 검색 키워드
  const [exerciseKeyword, setExerciseKeyword] = useState<string>('');

  // 운동 검색 리스트 표시 여부
  const [isOpenSearchList, setIsOpenSearchList] = useState<boolean>(true);

  // 선택한 운동 이름
  const [selectedExerciseName, setSelectedExerciseName] = useState<string>('');

  // 주요 운동 부위
  const [majorExercisePart, setMajorExercisePart] = useState<Part | ''>('');

  // 부가 운동 부위
  const [additionalExercisePart, setAdditionalExercisePart] = useState<
    Part | ''
  >('');

  // 기입 방식
  const [inputMethod, setInputMethod] = useState<SetTypeKey | ''>('');

  // 기입 방식 옵션
  const inputMethodOptions = [
    { label: '무게(kg) / 회', value: 'REPWEIGHT' },
    { label: '시간(sec) / 회', value: 'REPTIME' },
    { label: '회', value: 'REP' },
    { label: '시간', value: 'TIME' }
  ];

  const { mutate } = usePostCustomExerciseType({
    onSuccess: () => {
      showToast({
        type: 'success',
        message: '운동이 추가되었습니다'
      });
      setOpenAddCustomExerciseModal(false);
      queryClient.invalidateQueries(
        queryKeyFactory.GET_CUSTOM_EXERCISE_TYPES()
      );
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: '운동 추가에 실패했습니다'
      });
    }
  });

  // text => label
  const convertedExercisePartList = [
    { label: '전체', value: 'ALL' },
    { label: '가슴', value: 'CHEST' },
    { label: '등', value: 'BACK' },
    { label: '어깨', value: 'SHOULDER' },
    { label: '하체', value: 'LEG' },
    { label: '이두', value: 'BICEPS' },
    { label: '삼두', value: 'TRICEPS' },
    { label: '유산소', value: 'CARDIO' },
    { label: '코어', value: 'CORE' }
  ];

  const onMajorExercisePartChange = (value: string) => {
    setMajorExercisePart(value as Part);
  };

  const onAdditionalExercisePartChange = (value: string) => {
    setAdditionalExercisePart(value as Part);
  };

  const onInputMethodChange = (value: string) => {
    setInputMethod(value as SetTypeKey);
  };

  const onSubmit = () => {
    if (!selectedExerciseName) {
      showToast({
        type: 'fail',
        message: '운동 이름을 입력해주세요'
      });
    }

    if (!majorExercisePart) {
      showToast({
        type: 'fail',
        message: '운동 부위를 선택해주세요'
      });
    }

    if (!inputMethod) {
      showToast({
        type: 'fail',
        message: '기입 방식을 선택해주세요'
      });
    }

    const payload = {
      name: selectedExerciseName,
      part: majorExercisePart as Part,
      subPart: additionalExercisePart as Part,
      setType: inputMethod as SetTypeKey
    };

    mutate(payload);
  };

  const disabled =
    !selectedExerciseName ||
    !majorExercisePart ||
    !additionalExercisePart ||
    !inputMethod;

  return (
    <BottomSheet
      isOpen={openAddCustomExerciseModal}
      handleClose={() => setOpenAddCustomExerciseModal(false)}
      showHeader={false}
      closableBackground
      fullHeight
      bodyStyle={{ padding: '0px' }}
    >
      <PrevHeaderBarWithTitleTemplate
        title="운동 커스텀 추가"
        hasXButton
        onXButtonClick={() => setOpenAddCustomExerciseModal(false)}
        rightContents={
          <ButtonSubmit disabled={disabled} onClick={onSubmit}>
            추가
          </ButtonSubmit>
        }
      >
        <div className="flex flex-col gap-20 p-20">
          <div className="flex flex-col gap-12">
            <div>운동 이름</div>
            <div className="flex gap-8 items-center">
              <InputWrapper>
                <Input
                  onChange={e => {
                    setSelectedExerciseName(e.target.value);
                    setExerciseKeyword(e.target.value);
                    setIsOpenSearchList(true);
                  }}
                  value={selectedExerciseName}
                  placeholder="운동 이름을 입력해주세요"
                />
                {exerciseKeyword && (
                  <div
                    onClick={() => {
                      setSelectedExerciseName('');
                      setExerciseKeyword('');
                      setIsOpenSearchList(false);
                    }}
                    className="absolute right-5 top-1/2 transform -translate-y-1/2"
                  >
                    <Icon name="IconClose" width={16} height={16} />
                  </div>
                )}
              </InputWrapper>
            </div>

            <div style={{ display: isOpenSearchList ? 'block' : 'none' }}>
              <SearchList
                onClick={value => {
                  setSelectedExerciseName(value);
                  setIsOpenSearchList(false);
                }}
                exerciseKeyword={exerciseKeyword}
              />
            </div>
          </div>
          <div className="flex flex-col gap-12">
            <div>운동 부위</div>
            <div className="flex flex-col gap-4">
              <div className="text-[#746f7e] text-sm font-normal font-['Pretendard Variable'] leading-tight">
                주요
              </div>
              <Select
                onChange={onMajorExercisePartChange}
                selectContainerStyle={{ height: '53px' }}
                defaultValue="선택"
                options={convertedExercisePartList}
              />
            </div>
            <div className="flex flex-col gap-4">
              <div className="text-[#746f7e] text-sm font-normal font-['Pretendard Variable'] leading-tight">
                부가(선택)
              </div>
              <Select
                onChange={onAdditionalExercisePartChange}
                selectContainerStyle={{ height: '53px' }}
                defaultValue="선택"
                options={convertedExercisePartList}
              />
            </div>
          </div>
          <div className="flex flex-col gap-12">
            <div>기입 방식</div>
            <Select
              defaultValue="선택"
              onChange={onInputMethodChange}
              selectContainerStyle={{ height: '53px' }}
              options={inputMethodOptions}
            />
          </div>
        </div>
      </PrevHeaderBarWithTitleTemplate>
    </BottomSheet>
  );
};
